import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { useNotification } from '../../hooks/useNotification';
import { Palette, Save, ArrowLeft, Upload, Image, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Empresa } from '../../services/firebaseService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';

const LogoIdentidade: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError, NotificationComponent } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [primaryColor, setPrimaryColor] = useState<string>('#3B82F6');
  const [secondaryColor, setSecondaryColor] = useState<string>('#10B981');

  useEffect(() => {
    loadEmpresa();
  }, [userProfile?.empresaCnpj]);

  const loadEmpresa = async () => {
    if (!userProfile?.empresaCnpj) return;

    try {
      setLoading(true);
      const empresa = await firebaseService.getEmpresa(userProfile.empresaCnpj);
      
      if (empresa) {
        setLogoUrl(empresa.logoUrl || '');
        setLogoPreview(empresa.logoUrl || '');
        if (empresa.coresTema) {
          setPrimaryColor(empresa.coresTema.primary || '#3B82F6');
          setSecondaryColor(empresa.coresTema.secondary || '#10B981');
        }
      }
    } catch (error: any) {
      console.error('Erro ao carregar empresa:', error);
      showError('Erro ao carregar', 'Não foi possível carregar os dados da empresa.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      showError('Arquivo inválido', 'Por favor, selecione uma imagem.');
      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('Arquivo muito grande', 'A imagem deve ter no máximo 5MB.');
      return;
    }

    try {
      setUploading(true);
      
      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // TODO: Implementar upload para Firebase Storage ou outro serviço
      // Por enquanto, vamos usar a URL do preview como placeholder
      // Em produção, você deve fazer upload para Firebase Storage e obter a URL
      const uploadedUrl = await uploadToStorage(file);
      setLogoUrl(uploadedUrl);
      
      showSuccess('Logo carregado', 'A logo foi carregada com sucesso!');
    } catch (error: any) {
      console.error('Erro ao fazer upload:', error);
      showError('Erro ao fazer upload', error.message || 'Não foi possível fazer upload da logo.');
    } finally {
      setUploading(false);
    }
  };

  // Função para fazer upload para Firebase Storage
  const uploadToStorage = async (file: File): Promise<string> => {
    if (!userProfile?.empresaCnpj) {
      throw new Error('CNPJ da empresa não encontrado');
    }

    try {
      // Criar referência no Storage: empresas/{cnpj}/logo/{timestamp}-{filename}
      const timestamp = Date.now();
      const fileName = `logo-${timestamp}.${file.name.split('.').pop()}`;
      const storageRef = ref(storage, `empresas/${userProfile.empresaCnpj}/logo/${fileName}`);
      
      // Fazer upload do arquivo
      await uploadBytes(storageRef, file);
      
      // Obter URL de download
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error: any) {
      console.error('Erro ao fazer upload:', error);
      throw new Error(`Erro ao fazer upload: ${error.message}`);
    }
  };

  const handleRemoveLogo = () => {
    setLogoUrl('');
    setLogoPreview('');
  };

  const handleSave = async () => {
    if (!userProfile?.empresaCnpj) return;

    try {
      setSaving(true);
      await firebaseService.updateEmpresa(userProfile.empresaCnpj, {
        logoUrl: logoUrl,
        coresTema: {
          primary: primaryColor,
          secondary: secondaryColor
        }
      });
      
      // Aplicar cores imediatamente após salvar
      const root = document.documentElement;
      root.style.setProperty('--color-primary', primaryColor);
      root.style.setProperty('--color-primary-600', primaryColor);
      
      // Escurecer cor primária para hover
      const darkenColor = (color: string, amount: number): string => {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const newR = Math.max(0, Math.floor(r * (1 - amount)));
        const newG = Math.max(0, Math.floor(g * (1 - amount)));
        const newB = Math.max(0, Math.floor(b * (1 - amount)));
        return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
      };
      
      root.style.setProperty('--color-primary-700', darkenColor(primaryColor, 0.1));
      root.style.setProperty('--color-secondary', secondaryColor);
      root.style.setProperty('--color-secondary-600', secondaryColor);
      
      showSuccess('Configurações salvas', 'As configurações de logo e identidade foram salvas com sucesso!');
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      showError('Erro ao salvar', error.message || 'Não foi possível salvar as configurações.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <NotificationComponent />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/configuracoes')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Logo e Identidade</h1>
            <p className="text-gray-600">Personalize a identidade visual da sua empresa</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || uploading}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">
        {/* Logo */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Image className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Logo da Empresa</h2>
          </div>
          
          <div className="flex items-start gap-6">
            {/* Preview */}
            <div className="flex-shrink-0">
              {logoPreview ? (
                <div className="relative">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-w-48 max-h-48 object-contain"
                  />
                  <button
                    onClick={handleRemoveLogo}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center p-4">
                  <Image className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Nenhuma logo</p>
                </div>
              )}
            </div>

            {/* Upload */}
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-4">
                Faça upload da logo da sua empresa. Formatos aceitos: PNG, JPG, SVG. Tamanho máximo: 5MB.
              </p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors cursor-pointer">
                <Upload className="h-4 w-4" />
                {uploading ? 'Fazendo upload...' : 'Selecionar Logo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
              {logoUrl && (
                <p className="text-xs text-gray-500 mt-2">
                  Logo atual: {logoUrl.substring(0, 50)}...
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Cores */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Palette className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Cores do Tema</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cor Primária
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-16 h-16 rounded-lg border border-gray-300 cursor-pointer"
                />
                <div className="flex-1">
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="#3B82F6"
                  />
                  <p className="text-xs text-gray-500 mt-1">Usada em botões e elementos principais</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cor Secundária
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-16 h-16 rounded-lg border border-gray-300 cursor-pointer"
                />
                <div className="flex-1">
                  <input
                    type="text"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="#10B981"
                  />
                  <p className="text-xs text-gray-500 mt-1">Usada em elementos secundários</p>
                </div>
              </div>
            </div>
          </div>

          {/* Preview das cores */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-3">Preview das Cores</p>
            <div className="flex gap-3">
              <div
                className="flex-1 h-12 rounded-md flex items-center justify-center text-white font-medium"
                style={{ backgroundColor: primaryColor }}
              >
                Cor Primária
              </div>
              <div
                className="flex-1 h-12 rounded-md flex items-center justify-center text-white font-medium"
                style={{ backgroundColor: secondaryColor }}
              >
                Cor Secundária
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoIdentidade;

